import { useAuth0 } from "@auth0/auth0-react";
import { Navigate, useLocation } from "react-router-dom";
import React, { useState, useEffect } from "react";
import { CircularProgress, Box } from '@mui/material';
import { EmailVerificationRequired } from '../Verification/EmailVerificationRequired';
import { DomainVerificationRequired } from '../Verification/DomainVerificationRequired';
import { API_URLS } from '../../config';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

interface VerificationStatus {
  email_verified: boolean;
  domain_verified: boolean;
  domain_verification_pending: boolean;
  company_name?: string;
  company_domain?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, user, getAccessTokenSilently } = useAuth0();
  const location = useLocation();
  const [verificationLoading, setVerificationLoading] = useState(true);
  const [verificationStatus, setVerificationStatus] = useState<VerificationStatus | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);

  useEffect(() => {
    const checkVerification = async () => {
      if (!isAuthenticated || !user?.sub) {
        setVerificationLoading(false);
        return;
      }

      try {
        const token = await getAccessTokenSilently();

        // Fetch verification status
        const verificationResponse = await fetch(`${API_URLS.BASE}/auth/verification-status/${user.sub}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (verificationResponse.ok) {
          const status = await verificationResponse.json();
          setVerificationStatus(status);
        } else {
          // On backend error, fall back to the email_verified claim in the Auth0 ID token.
          // This prevents verified users from being locked out when the backend is unavailable.
          setVerificationStatus({
            email_verified: user?.email_verified ?? false,
            domain_verified: false,
            domain_verification_pending: false,
          });
        }

        // Fetch user roles to determine if recruiter
        const rolesResponse = await fetch(`${API_URLS.BASE}/auth/user-roles/${user.sub}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (rolesResponse.ok) {
          const { roles } = await rolesResponse.json();
          const isRecruiter = roles.some((role: any) => role.id === 'rol_ndBoJF3ROg0eczNi');
          setUserRole(isRecruiter ? 'recruiter' : 'interviewer');
        }
      } catch (error) {
        console.error('Error checking verification status:', error);
        setVerificationStatus({
          email_verified: false,
          domain_verified: false,
          domain_verification_pending: false,
        });
      } finally {
        setVerificationLoading(false);
      }
    };

    checkVerification();
  }, [isAuthenticated, user, getAccessTokenSilently]);

  if (isLoading || verificationLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    // Save the attempted URL for redirecting after login
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // Check email verification for all users
  if (verificationStatus && !verificationStatus.email_verified) {
    return <EmailVerificationRequired />;
  }

  // Check domain verification for recruiters
  // BUT allow access to onboarding and profile setup pages
  const isOnboardingOrProfileSetup =
    location.pathname === '/onboarding' ||
    location.pathname.includes('/recruiter/profile');

  if (userRole === 'recruiter' && verificationStatus && !verificationStatus.domain_verified && !isOnboardingOrProfileSetup) {
    // Only show domain verification if they have a company_domain set
    // (meaning they've created their profile)
    if (verificationStatus.company_domain) {
      return (
        <DomainVerificationRequired
          companyName={verificationStatus.company_name || 'your company'}
          companyDomain={verificationStatus.company_domain || ''}
          userEmail={user?.email || ''}
        />
      );
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute; 